import type { DocumentTab } from '../../../api/endpoints';
import type { InvoiceListSearch } from '../InvoiceListPage';

/** URL parameters that narrow the list (the search text `q` is independent of them). */
export const FACET_KEYS = [
  'status',
  'taxStatus',
  'from',
  'to',
  'customer',
  'minAmount',
  'maxAmount',
] as const;
export type FacetKey = (typeof FACET_KEYS)[number];

export type ViewId = 'all' | 'drafts' | 'sending' | 'attention' | 'valid' | 'deleted';

export interface PresetView {
  id: ViewId;
  /** The exact facet parameters of the view; every other facet is empty. */
  facets: Partial<Record<FacetKey, string>>;
}

/**
 * Preset views (BLOCK-1-REDESIGN §A.1): the definitions of the Today work-queue tiles, plus "all"
 * and "deleted drafts". Only facets: they never touch the search text, sort or page size.
 */
export const PRESET_VIEWS: PresetView[] = [
  { id: 'all', facets: {} },
  { id: 'drafts', facets: { status: 'draft' } },
  { id: 'sending', facets: { taxStatus: 'submitting,submitted' } },
  // A refused document stops needing attention once its correction is issued (WP-1b.2): it is then
  // `superseded`, so the view keeps the issued and signed ones only.
  {
    id: 'attention',
    facets: { status: 'issued,signed', taxStatus: 'invalid,rejected,not_accepted' },
  },
  { id: 'valid', facets: { taxStatus: 'valid' } },
  { id: 'deleted', facets: { status: 'discarded' } },
];

/** A comma list compared as a set: "submitted,submitting" equals "submitting,submitted". */
function sameValue(a: unknown, b: unknown): boolean {
  const norm = (v: unknown) =>
    (v === undefined || v === null ? '' : String(v))
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .sort()
      .join(',');
  return norm(a) === norm(b);
}

/** The view whose definition equals the URL's facets exactly, or null. */
export function activeView(search: InvoiceListSearch): ViewId | null {
  const match = PRESET_VIEWS.find((view) =>
    FACET_KEYS.every((key) => sameValue(search[key], view.facets[key])),
  );
  return match?.id ?? null;
}

/** How many facets the URL holds (shown as «تضييق النتائج (n)»). */
export function facetCount(search: InvoiceListSearch): number {
  return FACET_KEYS.filter((key) => Boolean(search[key])).length;
}

/**
 * D-6: the URL search after a document-family tab change — ONLY the tab is carried. The find panel
 * (the search text `q`, the seven facets, `sort`, `dir`, `page` and `limit`) resets, because the
 * families are different document registers: an invoices filter carried into the credit tab would
 * silently yield nothing and read as broken. The invoices tab is the default, so it is left off the
 * URL entirely.
 */
export function tabSearch(tab: DocumentTab): InvoiceListSearch {
  return tab === 'invoices' ? {} : { tab };
}

/** The URL search of a view: its facets only, every other facet cleared, first page. */
export function viewSearch(view: PresetView, current: InvoiceListSearch): InvoiceListSearch {
  const next: Record<string, unknown> = { ...current, page: undefined, customerName: undefined };
  for (const key of FACET_KEYS) next[key] = view.facets[key];
  return Object.fromEntries(
    Object.entries(next).filter(([, v]) => v !== undefined && v !== ''),
  ) as InvoiceListSearch;
}
